import {
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
  UnknownAction
} from '@reduxjs/toolkit/react'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'
import { emptyApi } from './services'
import storage from 'redux-persist/lib/storage'
import * as Sentry from '@sentry/react'
import { userSlice } from './slices'

// region: Reducers
const api = emptyApi.reducer
const user = userSlice.reducer

const appReducer = combineReducers({
  api,
  user
})

// endregion

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: UnknownAction
) => {
  if (action.type === 'user/logoutUser') {
    storage.removeItem('persist:root').then(() => sessionStorage.clear())
    return appReducer(undefined, action)
  }
  return appReducer(state, action)
}

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  blacklist: [emptyApi.reducerPath]
}
const sentryReduxEnhancer = Sentry.createReduxEnhancer({})
const persistedReducer = persistReducer(persistConfig, rootReducer)

/** Handles all async errors by default, taken from https://redux-toolkit.js.org/rtk-query/usage/error-handling */
export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
  if (isRejectedWithValue(action)) {
    console.warn(
      'Rejected action, problem with RTK Query results: ',
      action.error
    )
  }
  return next(action)
}

export type AppStore = ReturnType<typeof configureStore>
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredPaths: []
      }
    })
      .concat(emptyApi.middleware)
      .concat(rtkQueryErrorLogger),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer)
})

export default store
